import React, { useEffect, useState } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Select,
} from "@mui/material";

import styles from "../../../../styles/gri/general.module.css";
import { fixedPercentage } from "../../../../utils/common";
import { getRowErrorMessages } from "../../../../utils/getRowErrorMessage";

const Add = ({
  title,
  formData,
  table,
  units,
  inputData,
  disabled,
  setTable,
  defaultSegments,
  setFormData,
  errors,
  setErrors,
}) => {
  const [calculateProcess, setCalculateProcess] = useState({});

  // Calculate total quantity for all processes
  const calculateProcessTotal = () => {
    const newTotals = {};

    formData?.processes?.forEach((segment) => {
      let total = 0;
      segment?.emissions?.forEach((emission) => {
        const quantity = parseFloat(emission.quantity) || 0;
        total += quantity;
      });
      newTotals[segment.processName] = total;
    });

    setCalculateProcess(newTotals);
  };

  useEffect(() => {
    calculateProcessTotal();
  }, [formData]);

  const handleChange = (e, rowIndex, emissionIndex) => {
    const { name, value } = e.target;

    // Allow only numbers and decimal for quantity
    if (name === "quantity" && !/^\d*\.?\d*$/.test(value)) return;

    // 1. Update formData
    setFormData((prevState) => ({
      ...prevState,
      processes: prevState.processes.map((item, idx) => {
        if (idx !== rowIndex) return item;

        const updatedEmissions = [...item.emissions];
        if (
          updatedEmissions[emissionIndex] &&
          updatedEmissions[emissionIndex].hasOwnProperty(name)
        ) {
          updatedEmissions[emissionIndex][name] = value;
        }

        return {
          ...item,
          emissions: updatedEmissions,
        };
      }),
    }));

    // 2. Update table
    setTable((prevTable) => {
      const updatedTable = [...prevTable];
      const rowIndexInTable = updatedTable.findIndex(
        (row) => row.index === rowIndex
      );
      if (rowIndexInTable >= 0) {
        updatedTable[rowIndexInTable][name] = value;
      }
      return updatedTable;
    });

    // 3. Remove the error if data is entered
    const errorKey = `${name}_${rowIndex}_${emissionIndex}`;
    if (value.trim() !== "" && errors?.[errorKey]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const getRowAndColumnIndex = (fieldName) => {
    // Handle different field name formats:
    // unit_0_0 (row 0, column 0)
    // emissionsName_1_0 (row 1, column 0)
    // processes.processName.1 (row 1)
    // airEmissions.1 (row 1)

    const parts = fieldName.split("_");

    if (fieldName.includes(".")) {
      // Format like "processes.processName.1" or "airEmissions.1"
      const lastPart = fieldName.split(".").pop();
      const rowIndex = parseInt(lastPart, 10);
      return { rowIndex, columnIndex: null }; // No column index for these
    }

    if (parts.length === 3) {
      // Format like "unit_0_0" or "quantity_0_1"
      return {
        rowIndex: parseInt(parts[1], 10),
        columnIndex: parseInt(parts[2], 10),
      };
    }

    if (parts.length === 2) {
      // Format like "airEmissions_1"
      return {
        rowIndex: parseInt(parts[1], 10),
        columnIndex: null,
      };
    }

    return { rowIndex: -1, columnIndex: -1 }; // Not a row/column error
  };

  const groupErrorsByRow = (errors) => {
    const rowErrors = {};

    Object.entries(errors).forEach(([fieldName, errorMsg]) => {
      const normalizedError =
        typeof errorMsg === "string" ? errorMsg.trim() : "";
      if (!normalizedError) return;
      if (
        fieldName.startsWith("airEmissions") ||
        fieldName.startsWith("processes")
      )
        return;
      const { rowIndex, columnIndex } = getRowAndColumnIndex(fieldName);

      if (rowIndex >= 0) {
        if (!rowErrors[rowIndex]) {
          rowErrors[rowIndex] = {};
        }

        // Group by column index if it exists
        const columnKey =
          columnIndex !== null ? `column_${columnIndex}` : "general";

        if (!rowErrors[rowIndex][columnKey]) {
          rowErrors[rowIndex][columnKey] = [];
        }

        rowErrors[rowIndex][columnKey].push(normalizedError);
      }
    });

    return rowErrors;
  };

  return (
    <>
      <p className={styles.ques_heading} style={{ marginTop: "2rem" }}>
        {title}
      </p>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "10px",
          width: "98.5%",
        }}
      >
        <Table
          sx={{
            border: "1px solid #ddd",
            borderRadius: "10px",
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#1C486B",
                border: "1px solid #ddd",
              }}
            >
              {inputData?.map((section, tableIndex) => (
                <TableCell
                  key={tableIndex}
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    width: "20%",
                    border: "1px solid #ddd",
                    overflow: "hidden",
                  }}
                >
                  {section?.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {table?.map((segment, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {segment?.emissions.map((item, emissionIndex) => (
                  <TableRow key={`${rowIndex}-${emissionIndex}`}>
                    {emissionIndex === 0 && (
                      <TableCell
                        sx={{
                          border: "1px solid #DBDBDB",
                          width: "8rem",
                          py: "0.5rem",
                          maxWidth: "8rem",
                          wordWrap: "break-word",
                          width: "20%",
                          overflow: "hidden",
                        }}
                        rowSpan={(segment?.emissions?.length || 0) + 1}
                      >
                        <Typography
                          placeholder="Process Name"
                          sx={{
                            border: "none",
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "inter",
                          }}
                        >
                          {segment?.processName}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        border: "1px solid #DBDBDB",
                        background: "white",
                        py: "0.5rem",
                        width: "20%",
                        overflow: "hidden",
                      }}
                    >
                      <TextField
                        sx={{
                          width: "100%",
                          py: "0.5rem",
                          fontSize: "14px",
                          fontWeight: 500,
                          fontFamily: "inter",
                          "& .MuiInputBase-root": {
                            overflowY: "auto",
                            cursor: "default",
                          },
                          "& .MuiInputBase-root:hover": {
                            cursor: "pointer",
                          },
                        }}
                        value={
                          formData?.processes[
                            rowIndex
                          ]?.item?.emissionsName?.includes(";")
                            ? formData.processes[
                              rowIndex
                            ].item.emissionsName.split(";")[1]
                            : formData?.processes[rowIndex]?.item
                              ?.emissionsName ||
                            (item?.emissionsName?.includes(";")
                              ? item?.emissionsName.split(";")[1]
                              : item?.emissionsName)
                        }
                        placeholder="Enter Text"
                        type="text"
                        multiline
                        minRows={1}
                        size="small"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        border: "1px solid #DBDBDB",
                        background: "white",
                        py: "0.5rem",
                        width: "20%",
                        overflow: "hidden",
                      }}
                    >
                      <TextField
                        sx={{
                          width: "6.5rem",
                          py: "0.5rem",
                          fontSize: "14px",
                          fontWeight: 500,
                          fontFamily: "inter",
                          "& .MuiInputBase-root": {
                            overflowY: "auto",
                            cursor: "default",
                          },
                          "& .MuiInputBase-root:hover": {
                            cursor: "pointer",
                          },
                        }}
                        name="quantity"
                        disabled={
                          !segment?.processName ||
                          !(
                            formData?.processes[rowIndex]?.emissions?.[
                              emissionIndex
                            ]?.emissionsName || item?.emissionsName
                          )
                        }
                        value={
                          formData?.processes[rowIndex]?.emissions[
                            emissionIndex
                          ]?.quantity || ""
                        }
                        onChange={(e) =>
                          handleChange(e, rowIndex, emissionIndex)
                        }
                        placeholder="Enter Number"
                        type="text"
                        multiline
                        minRows={1}
                        size="small"
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        border: "1px solid #DBDBDB",
                        background: "white",
                        py: "0.5rem",
                        height: "28px",
                        px: 0,
                        overflow: "hidden",
                      }}
                    >
                      <Select
                        sx={{
                          height: "28px",
                          py: "0.5rem",
                          border: "none",
                          width: "8.5rem",
                          px: 0,
                          fontSize: "14px",
                          fontWeight: 500,
                          fontFamily: "inter",
                          boxShadow: "none",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        name="unit"
                        value={
                          formData?.processes[rowIndex]?.emissions[
                            emissionIndex
                          ]?.unit || ""
                        }
                        onChange={(e) =>
                          handleChange(e, rowIndex, emissionIndex)
                        }
                        disabled={disabled ? disabled : false}
                        displayEmpty
                        size="small"
                        fullWidth
                      >
                        <MenuItem value="" disabled>
                          <em>Select</em>
                        </MenuItem>
                        {units?.map((unit, idx) => (
                          <MenuItem key={idx} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Total Row after each process */}
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    py: "0.5rem",
                  }}
                >
                  <TableCell
                    colSpan={1}
                    sx={{
                      border: "1px solid #DBDBDB",
                      textAlign: "left",
                      py: "0.5rem",
                      height: "28px",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "inter",
                      width: "20%",
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #DBDBDB",
                      fontWeight: "bold",
                      width: "20%",
                      whiteSpace: "normal", // allow text wrapping
                      wordWrap: "break-word", // ensure breaking
                      overflowWrap: "break-word", // ensure breaking for long words
                      lineHeight: "1.5",
                    }}
                  >
                    {fixedPercentage(
                      calculateProcess[segment?.processName] || 0
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "1px solid #DBDBDB",
                      width: "10rem",
                      maxWidth: "10rem",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "Inter",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {/* {segment?.emissions?.[0]?.unit || ""} */}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 1 }}>
        {Object.entries(groupErrorsByRow(errors)).map(
          ([rowIndex, rowErrors]) => (
            <div key={`row-${rowIndex}`}>
              {Object.entries(rowErrors).map(([columnKey, columnErrors]) => (
                <Box key={`row-${rowIndex}-${columnKey}`} sx={{ ml: 0 }}>
                  <Typography
                    // key={`error-${rowIndex}-${columnKey}-${idx}`}
                    variant="caption"
                    sx={{
                      color: "#d34040",
                      fontSize: "12px",
                      display: "block",
                      // ml: columnKey.startsWith("column_") ? 4 : 2,
                    }}
                  >
                    Row {Number(rowIndex) + 1} errors:
                    {/* {columnErrors.join(", ")} */}
                    {
                      [...new Set(
                        columnErrors.filter(
                          err =>
                            err === "Quantity is required." ||
                            err === "Unit is required."
                        )
                      )].join(", ")
                    }
                  </Typography>
                </Box>
              ))}
            </div>
          )
        )}
      </Box>
    </>
  );
};

export default Add;
